import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { ThemeService } from './theme/theme.service';
import { ProjectType } from '../models/enums';
import { Engine } from '../engine/engine';
import { UnitConversionService } from '@infinitform/shared-ui';

export interface EngineInitConfig {
  canvas: HTMLCanvasElement;
  accelerationCanvas: HTMLCanvasElement;
  viewHelper: HTMLElement;
  css2dContainer: HTMLElement;
}

@Injectable({ providedIn: 'root' })
export class EngineInitializerService {
  private session = inject(SessionService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private unitConversionService = inject(UnitConversionService);

  async initUserSettings(): Promise<void> {
    this.session.progressService.startPhase('user-settings', 'Loading user settings...');
    this.session.progressService.updateProgress(5, 'Loading user settings...');

    const settings: { mouse: unknown; graphics: unknown; keyboardShortcuts?: unknown } = (await this.session.getUserSettings()) as { mouse: unknown; graphics: unknown; keyboardShortcuts?: unknown };
    this.session.currentMousePreset = settings.mouse;
    this.session.currentGraphicsSettings = settings.graphics;
    if (settings.keyboardShortcuts) {
      this.session.keyboardShortcuts.importFromSync(settings.keyboardShortcuts);
    }

    this.session.progressService.updateProgress(10, 'User settings loaded');
    this.session.progressService.completeTask('user-settings', 'User settings loaded');
  }

  async initializeEngine(config: EngineInitConfig, skipModelsLoading?: boolean): Promise<Engine | null> {
    try {
      // Start the project setup phase
      this.session.progressService.startPhase('project-setup', 'Initializing project...');

      console.log('[EngineInit] Starting initializeEngine...');
      const project = (await this.session.getProject()) as { status: number; proto: string; projectType: ProjectType; error?: string; code?: string; ai_analysis_pending?: boolean };

      console.log('[EngineInit] getProject result — status:', project?.status, '| projectType:', project?.projectType, '| proto:', project?.proto, '| has optimization:', !!(project as any)?.optimization);

      if (!project) {
        console.error('[EngineInit] No project returned — redirecting to flow-status');
        this.router.navigateByUrl('/flow-status', {
          state: { type: 'error' }
        });
        return null;
      } else if ('error' in project && 'code' in project) {
        console.error('[EngineInit] Project has error:', project.error, '| code:', project.code);
        this.router.navigateByUrl('/flow-status', {
          state: {
            title: 'Shared Resource',
            message: project.error,
            type: 'error'
          }
        });
        return null;
      } else if (![1, 2, 4].includes(project.status)) {
        if (project.projectType === ProjectType.SoftJaws) {
          // TODO: Handle soft jaws project status
        } else {
          const isFea = (project as any).config?.runType === 1;
          this.router.navigateByUrl('/flow-status', {
            state: {
              type: 'in-progress',
              title: isFea ? 'FEA in Progress' : 'Optimization in Progress',
            }
          });
        }
        return null;
      }

      // Multi-manufacturing-method: if the user navigated directly to a
      // CHILD project (via stale bookmark, admin link, or manual URL edit),
      // transparently redirect to the master. Children are never visible to
      // users per design #4 — the redirect rescues stale URLs without
      // breaking them.
      const masterProjectId = (project as { masterProjectId?: string | null }).masterProjectId;
      if (masterProjectId) {
        this.router.navigateByUrl(`/editor/${masterProjectId}`, { replaceUrl: true });
        return null;
      }

      this.session.setProject(project);

      const engine = new Engine(
        config.canvas,
        config.accelerationCanvas,
        config.viewHelper as HTMLDivElement,
        config.css2dContainer as HTMLDivElement,
        this.session,
        this.themeService,
        this.unitConversionService
      );

      // Complete project setup phase and start assembly loading phase
      this.session.progressService.completePhase();
      this.session.progressService.startPhase('assembly-loading', 'Loading assembly...');

      engine.progressUpdate.subscribe(({ total, current }) => {
        this.session.assemblyInitProgress.total = total;
        this.session.assemblyInitProgress.current = current;

        // Only update progress if we're still in the assembly-loading phase
        if (this.session.progressService.getCurrentPhase() === 'Assembly Loading') {
          const progressRatio = total > 0 ? current / total : 0;
          const subProgress = 40 + progressRatio * 10;
          this.session.progressService.updateProgress(subProgress, `Initializing assembly... ${current}/${total}`);
        }
      });

      console.log('[EngineInit] Loading assembly from proto:', project.proto);
      await engine.setAssembly(await this.session.getAssembly(project.proto));
      console.log('[EngineInit] Assembly loaded successfully');

      if (project.projectType === ProjectType.TopologyOptimization) {
        if (!skipModelsLoading) {
          // Multi-manufacturing-method master: the master has no results of
          // its own (by design — all results live under children). Pre-merge
          // the first Done child's `optimization` URL block into
          // `session.project.optimization` so the subsequent optimized-
          // assembly loading block reads valid URLs instead of empty ones.
          const cases = (this.session.project as { manufacturingMethods?: Array<{ methodId: string }> })
            .manufacturingMethods;
          const childIds = (this.session.project as { childProjectIds?: string[] })
            .childProjectIds;
          if (
            cases && cases.length >= 1 &&
            childIds && childIds.length >= 1 &&
            !this.session.project.optimization?.additive_design
          ) {
            console.log('[EngineInit] Multi-manufacturing master detected — pre-merging first Done child optimization...');
            try {
              const children = await this.session.fetchMasterChildren();
              const firstDone = (children as Array<{ id: string; status: number }>)
                .find((c) => c.status === 4);
              if (firstDone) {
                await this.session.mergeChildOptimization(firstDone.id);
                console.log('[EngineInit] Pre-merged child', firstDone.id, 'optimization into master');
              }
            } catch (err) {
              console.warn('[EngineInit] Multi-manufacturing pre-merge failed (continuing):', err);
            }
          }

          if (project.status === 4 || (this.session.project as { runSummary?: { done?: number } }).runSummary?.done) {
            console.log('[EngineInit] Status 4 / done children — loading optimization data...');
            console.log('[EngineInit] optimization.additive_design:', (this.session.project as any).optimization?.additive_design || '(empty)');
            console.log('[EngineInit] optimization.additive_design_parametric:', (this.session.project as any).optimization?.additive_design_parametric || '(empty)');
            console.log('[EngineInit] optimization.v2:', JSON.stringify((this.session.project as any).optimization?.v2)?.substring(0, 300));
            // Complete assembly loading phase and start optimization data phase
            this.session.progressService.completePhase();
            this.session.progressService.startPhase('optimization-data', 'Loading optimization data...');
            this.session.progressService.clearTasks();

            // Start parallel tasks with weights
            this.session.progressService.startTask('optimized-assembly', 30, 'Starting optimized assembly download...');
            this.session.progressService.startTask('optimization-info', 40, 'Starting optimization info loading...');
            this.session.progressService.startTask(
              'parametric-assembly',
              20,
              'Starting parametric assembly download...'
            );
            this.session.progressService.startTask('reaction-forces', 10, 'Starting reaction forces loading...');

            const results = await Promise.allSettled([
              this.session.getOptimizedAssembly(),
              this.session.getOptimizedAssemblyInfo(),
              this.session.getOptimizedAssemblyParametric(),
              this.session.getReactionForces()
            ]);

            console.log('[EngineInit] Promise.allSettled results:');
            results.forEach((r, i) => {
              const names = ['optimizedAssembly', 'optimizedInfo', 'parametricAssembly', 'reactionForces'];
              if (r.status === 'fulfilled') {
                console.log(`[EngineInit]   ${names[i]}: fulfilled`);
              } else {
                console.error(`[EngineInit]   ${names[i]}: REJECTED —`, r.reason);
              }
            });

            // Handle optimized assembly
            if (results[0].status === 'fulfilled') {
              await engine.setOptimizedAssembly(results[0].value);
              this.session.organicAssemblyAvailable = true;
            } else {
              this.session.organicAssemblyAvailable = false;
            }

            // Handle optimized info
            if (results[1].status === 'fulfilled') {
              engine.setOptimizedInfo(results[1].value);
              this.session.optimizedInfoAvailable = true;
            } else {
              this.session.optimizedInfoAvailable = false;
            }

            // Handle optimized parametric assembly
            if (results[2].status === 'fulfilled') {
              await engine.setOptimizedAssemblyParametric(results[2].value);
              this.session.parametricAssemblyAvailable = true;
            } else {
              this.session.parametricAssemblyAvailable = false;
            }

            // Handle reaction forces loading
            if (results[3].status === 'fulfilled') {
              // Reaction forces loaded successfully
            }
          }
        }
      }

      console.log('[EngineInit] Calling engine.init with status:', project.status);
      await engine.init(project.status);
      this.session.setEngine(engine);

      // Apply saved part visibility settings
      this.session.applyPartVisibility();

      // Complete the optimization data phase and finish loading
      this.session.progressService.updateMessage('Project loaded successfully');
      this.session.progressService.completePhase();

      this.session.loadingProject = false;
      console.log('[EngineInit] Engine initialization complete');

      return engine;
    } catch (err) {
      console.error('[EngineInit] FATAL ERROR — redirecting to flow-status:', err);
      this.router.navigateByUrl('/flow-status', {
        state: { type: 'error' }
      });
      return null;
    }
  }
}
